import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { apiCall } from 'modules/core/services/api/api'
import type { CreationStreamCardDto } from 'openapi/models/CreationStreamCardDto'
import { CreationIntentService } from 'openapi/services/CreationIntentService'
import { safeGetLocalStorage, safeSetLocalStorage } from 'modules/core/services/safe-local-storage'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { creativeHasRenderableMedia, isStillMaking } from '../utils/suggestion-status'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { EditsApplyingBadge } from './EditsApplyingBadge'
import { SHAPE_META } from './ShapeSlider'
import { SparkBlueprint } from './spark-blueprints'
import { useBrandKitStore } from '../store/brand-kit-store'
import { useIdeaStore, type PostIdea } from '../store/idea-store'

/** The post's own voice for the card line: caption first, idea as fallback. */
function draftLine(idea: PostIdea): string {
  const content = idea.primaryCreative?.content as unknown as Record<string, unknown> | undefined
  const caption = typeof content?.caption === 'string' ? content.caption : ''
  const firstSentence = caption.split(/\n/)[0]?.trim()
  return firstSentence || idea.idea
}

/**
 * The card's media label, derived from what the draft ACTUALLY contains — not from the
 * project's intended shape. A generation that fell back (e.g. a reel idea that produced a
 * static image) must not promise "Reel" on the card and open as an image post.
 */
function draftMediaLabel(idea: PostIdea): 'Reel' | 'Carousel' | 'Image' {
  const content = idea.primaryCreative?.content as unknown as Record<string, unknown> | undefined
  if (content) {
    if (typeof content.videoUrl === 'string' && content.videoUrl) return 'Reel'
    const items = content.mediaItems as unknown[] | undefined
    if (Array.isArray(items) && items.length >= 2) return 'Carousel'
    if (typeof content.imageUrl === 'string' && content.imageUrl) return 'Image'
  }
  return idea.shape === 'carousel'
    ? 'Carousel'
    : idea.shape === 'short_vertical_video'
      ? 'Reel'
      : 'Image'
}

/** The thumbnail is the last-rendered master while composition edits are still
 *  being applied server-side — drafts here are never published, no gate needed. */
function draftEditsApplying(idea: PostIdea): boolean {
  return idea.primaryCreative?.content?.compositionPendingRender === true
}

/** The master render reached its terminal failure (server-stamped); Retry re-renders. */
function draftEditsRenderFailed(idea: PostIdea): boolean {
  return draftEditsApplying(idea) && !!idea.primaryCreative?.content?.compositionRenderFailed
}

/** A media URL the card can thumbnail (first carousel slide or the image). */
function draftThumb(idea: PostIdea): string | null {
  const content = idea.primaryCreative?.content as unknown as Record<string, unknown> | undefined
  if (!content) return null
  const items = content.mediaItems as Array<{ url?: string; type?: string }> | undefined
  const firstImage = items?.find(m => m.type === 'image' && m.url)?.url
  return firstImage ?? (typeof content.imageUrl === 'string' ? content.imageUrl : null)
}

// ─── Skip decay (client-side, localStorage) ────────────────────────────────
// A format the user keeps SEEING but never taps is fatigued: after
// FATIGUE_AFTER_DAYS distinct days on screen without a tap it drifts to the
// back of the rail. A tap clears the fatigue (they engaged). Impressions
// count once per day so re-renders don't inflate them. Server-side ranking
// (vertical fit + rotation) stays authoritative WITHIN the fresh/fatigued
// groups.
const IMPRESSIONS_KEY = 'taka:streamImpressions'
const FATIGUE_AFTER_DAYS = 5

type ImpressionMap = Record<string, { seen: number; lastSeenDay: string }>

function readImpressions(): ImpressionMap {
  try {
    const parsed: unknown = JSON.parse(safeGetLocalStorage(IMPRESSIONS_KEY) ?? '{}')
    // Guard valid-JSON non-objects ('null', numbers): a corrupted cache must
    // never crash the rail — best-effort means best-effort.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as ImpressionMap)
      : {}
  } catch {
    return {}
  }
}

function recordImpression(formatId: string): void {
  const day = new Date().toISOString().slice(0, 10)
  const map = readImpressions()
  const entry = map[formatId]
  if (entry?.lastSeenDay === day) return
  map[formatId] = { seen: (entry?.seen ?? 0) + 1, lastSeenDay: day }
  safeSetLocalStorage(IMPRESSIONS_KEY, JSON.stringify(map))
}

function clearFatigue(formatId: string): void {
  const map = readImpressions()
  delete map[formatId]
  safeSetLocalStorage(IMPRESSIONS_KEY, JSON.stringify(map))
}

/**
 * The creation stream, v1 of the "Inspire me" door: proven post formats with
 * honest WHY lines, rotated daily per business. Tapping a card prefills the
 * hero prompt (editable, combinable with dropped photos) — one-tap generation
 * waits for brand-rendered previews (P2 next phase, see DESIGN.md). A dead or
 * slow stream renders nothing and never blocks the hero.
 */
export function StreamRail({
  onPick,
}: {
  onPick: (card: CreationStreamCardDto, position: number) => void
}) {
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const [cards, setCards] = useState<CreationStreamCardDto[]>([])
  const trackedViewRef = useRef(false)
  // The tap copy is the transformation promise: blueprint in, THEIR post out.
  const businessName = useBrandKitStore(s => s.brandKit.businessName)

  // Taka's lane: topic drafts the engine ALREADY generated (real previews, no
  // extra cost). They lost their home surface when the topic grid retired —
  // here they lead the rail, and a tap opens the same /post/:id arrival view
  // as a fresh intent. User-authored intent drafts stay out (they're the
  // user's work-in-progress on /posts, not inspiration).
  const allIdeas = useIdeaStore(s => s.ideas)
  const fetchIdeas = useIdeaStore(s => s.fetchIdeas)
  // Home hydrates the idea store from the TOPICS payload only, which can't
  // see topic-less intent drafts — without this fetch the resume strip would
  // stay empty on a cold load.
  useEffect(() => {
    fetchIdeas()
  }, [fetchIdeas])
  // The owner's own unscheduled intent drafts. They deliberately stay OUT of
  // the Taka lane (that's inspiration; these are THEIR work), but vanishing
  // from Home entirely made a hero-created draft feel lost the moment you
  // left the arrival view. A slim resume strip closes that loop.
  // Posts Taka is making RIGHT NOW (first-creation window / a reel still filming).
  // They can't thumbnail yet, so the draft lanes below would drop them — this strip
  // is the "Taka is already on it" signal a fresh user needs in the first minutes.
  // The !draftThumb guard keeps a post out of two lanes at once (a legacy reel gets
  // its poster mid-render, long before its status settles — it shows as a draft).
  const making = allIdeas
    .filter(
      i =>
        i.status === 'draft' &&
        !i.isRegenerating &&
        isStillMaking(i.primaryCreative) &&
        !draftThumb(i)
    )
    .slice(0, 4)
  const wipDrafts = allIdeas
    .filter(
      i =>
        i.source === 'intent' &&
        i.topicId === null &&
        i.status === 'draft' &&
        !i.isRegenerating &&
        creativeHasRenderableMedia(i.primaryCreative) &&
        !!draftThumb(i)
    )
    .slice(0, 4)
  const drafts = allIdeas
    .filter(
      i =>
        i.topicId !== null &&
        i.status === 'draft' &&
        !i.isRegenerating &&
        creativeHasRenderableMedia(i.primaryCreative) &&
        !!draftThumb(i)
    )
    .slice(0, 4)

  useEffect(() => {
    let cancelled = false
    apiCall(CreationIntentService.creationIntentControllerStreamV1, null)
      .then(res => {
        if (!cancelled && res?.cards?.length) setCards(res.cards)
      })
      .catch(() => {
        // Best-effort rail: the hero is the page's core, the stream never blocks it.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Fatigued formats drift to the back; server order preserved within groups.
  const orderedCards = useMemo(() => {
    const map = readImpressions()
    const fatigued = (c: CreationStreamCardDto) => (map[c.id]?.seen ?? 0) >= FATIGUE_AFTER_DAYS
    return [...cards.filter(c => !fatigued(c)), ...cards.filter(fatigued)]
  }, [cards])

  useEffect(() => {
    if (!cards.length || trackedViewRef.current) return
    trackedViewRef.current = true
    track(AnalyticsEvents.Home.HOME_STREAM_VIEWED, { card_count: cards.length })
  }, [cards, track])

  // Impressions are VIEWPORT-TRUE: a card only earns a fatigue day when at
  // least half of it was actually on screen (the rail sits below the fold and
  // mobile shows ~1.5 cards). Bulk-counting on mount fatigued formats the
  // user never saw. One observer, per-card data-format-id targets.
  const gridRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const root = gridRef.current
    if (!root || !orderedCards.length || typeof IntersectionObserver === 'undefined') return
    const seenThisMount = new Set<string>()
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const id = (entry.target as HTMLElement).dataset.formatId
          if (!id || seenThisMount.has(id)) continue
          seenThisMount.add(id)
          recordImpression(id)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.5 }
    )
    for (const el of root.querySelectorAll('[data-format-id]')) observer.observe(el)
    return () => observer.disconnect()
  }, [orderedCards])

  if (!cards.length && !drafts.length && !wipDrafts.length && !making.length) return null

  return (
    <section className="space-y-6">
      {making.length > 0 && (
        <div>
          <div className="mb-2 flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-paper">
              Taka is making these now
            </h2>
            <span className="text-[13px] text-paper-faint">
              They land here the moment they&apos;re ready.
            </span>
          </div>
          <div className="no-scrollbar -mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
            {making.map((draft, index) => (
              <button
                key={draft.projectId}
                type="button"
                onClick={() => {
                  track(AnalyticsEvents.Home.HOME_STREAM_CARD_TAPPED, {
                    format_id: 'making_draft',
                    position: index,
                  })
                  navigate(`/post/${draft.projectId}`)
                }}
                className="flex max-w-[300px] min-w-0 flex-shrink-0 snap-start items-center gap-2.5 rounded-full border border-line bg-bg-2 py-1.5 pr-4 pl-1.5 text-left transition-colors hover:border-acid/50"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-bg-3">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-acid" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] leading-tight font-medium text-paper">
                    {draftLine(draft)}
                  </span>
                  <span className="block text-[11px] text-paper-faint">
                    {draftMediaLabel(draft)} · making it now
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {wipDrafts.length > 0 && (
        <div>
          <div className="mb-2 flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-paper">
              Pick up where you left off
            </h2>
            <span className="text-[13px] text-paper-faint">
              Drafts you started. Tap to finish and post.
            </span>
          </div>
          <div className="no-scrollbar -mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
            {wipDrafts.map((draft, index) => (
              <button
                key={draft.projectId}
                type="button"
                onClick={() => {
                  track(AnalyticsEvents.Home.HOME_STREAM_CARD_TAPPED, {
                    format_id: 'own_draft',
                    position: index,
                  })
                  navigate(`/post/${draft.projectId}`)
                }}
                className="flex max-w-[300px] min-w-0 flex-shrink-0 snap-start items-center gap-2.5 rounded-full border border-line bg-bg-2 py-1.5 pr-4 pl-1.5 text-left transition-colors hover:border-acid/50"
              >
                <span className="relative h-9 w-9 flex-shrink-0">
                  <img
                    src={draftThumb(draft)!}
                    alt=""
                    loading="lazy"
                    className="h-full w-full rounded-full object-cover"
                    draggable={false}
                  />
                  {/* The 36px pill can't fit the full chip — a mini spinner +
                      honest subtitle below carry the "edits still applying" signal. */}
                  {draftEditsApplying(draft) && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-bg/60">
                      {draftEditsRenderFailed(draft) ? (
                        <AlertTriangle size={14} className="text-destructive" />
                      ) : (
                        <Loader2
                          size={14}
                          className="animate-spin text-acid motion-reduce:animate-none"
                        />
                      )}
                    </span>
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] leading-tight font-medium text-paper">
                    {draftLine(draft)}
                  </span>
                  <span className="block text-[11px] text-paper-faint">
                    {draftMediaLabel(draft)} ·{' '}
                    {draftEditsRenderFailed(draft)
                      ? "edits didn't apply"
                      : draftEditsApplying(draft)
                        ? 'applying your edits'
                        : 'draft'}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <div>
          <div className="mb-3 flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-paper">Fresh from Taka</h2>
            <span className="text-[13px] text-paper-faint">
              Ready drafts made for your business. Tap to review and post.
            </span>
          </div>
          <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-4 md:overflow-visible md:px-0">
            {drafts.map((draft, index) => (
              <button
                key={draft.projectId}
                type="button"
                onClick={() => {
                  track(AnalyticsEvents.Home.HOME_STREAM_CARD_TAPPED, {
                    format_id: 'taka_draft',
                    position: index,
                  })
                  navigate(`/post/${draft.projectId}`)
                }}
                className="group w-[220px] flex-shrink-0 snap-start overflow-hidden rounded-[12px] border border-line bg-bg-2 text-left transition-colors hover:border-acid/50 md:w-auto"
              >
                <div className="relative aspect-square w-full overflow-hidden bg-bg-3">
                  <img
                    src={draftThumb(draft)!}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    draggable={false}
                  />
                  {/* The thumb lags the user's composition edits until the
                      master re-renders — honest chip beats a silent revert. */}
                  {draftEditsApplying(draft) && (
                    <EditsApplyingBadge overlay creativeId={draft.primaryCreative?.id} />
                  )}
                </div>
                <div className="flex flex-col gap-1 p-3">
                  <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-paper-faint uppercase">
                    <span className="h-1.5 w-1.5 rounded-full bg-acid" aria-hidden="true" />
                    Ready · {draftMediaLabel(draft)}
                  </span>
                  <span className="line-clamp-2 text-[13.5px] leading-snug font-medium text-paper">
                    {draftLine(draft)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {cards.length > 0 && (
        <div ref={gridRef}>
          <div className="mb-3 flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-paper">Need a spark?</h2>
            <span className="text-[13px] text-paper-faint">
              Proven post recipes. Tap one and Taka crafts it for your brand.
            </span>
          </div>
          <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-4 md:overflow-visible md:px-0">
            {orderedCards.map((card, index) => {
              const meta =
                SHAPE_META[card.shape as keyof typeof SHAPE_META] ?? SHAPE_META.static_image
              return (
                <button
                  key={card.id}
                  type="button"
                  data-format-id={card.id}
                  onClick={() => {
                    track(AnalyticsEvents.Home.HOME_STREAM_CARD_TAPPED, {
                      format_id: card.id,
                      position: index,
                    })
                    clearFatigue(card.id)
                    onPick(card, index)
                  }}
                  className="group flex w-[220px] flex-shrink-0 snap-start flex-col overflow-hidden rounded-[12px] border border-line bg-bg-2 text-left transition-colors hover:border-acid/50 hover:bg-bg-3 md:w-auto"
                >
                  <SparkBlueprint formatId={card.id} />
                  <span className="flex flex-1 flex-col gap-2 p-4">
                    <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-paper-faint uppercase">
                      <meta.Icon size={13} />
                      {meta.label}
                    </span>
                    <span className="text-[14px] leading-snug font-semibold text-paper">
                      {card.title}
                    </span>
                    <span className="text-[12px] leading-snug text-paper-faint">
                      {card.whyLine}
                    </span>
                    <span className="mt-auto truncate pt-1 text-[12.5px] font-medium text-acid opacity-0 transition-opacity group-hover:opacity-100 md:opacity-0">
                      Craft it for {businessName || 'my brand'} →
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}
